/* flowjs */

import {
  IAdContainerPort,
  OgyResolveIntentCallback
} from '../../domain/ports/ad-container.port';
import {
  StateListener,
  ViewabilityListener,
  ExposureListener,
  SizeChangeListener,
  ErrorListener,
  NavigationEventListener,
  AdContainerState
} from '../../domain/ad-container.domain';

import {
  AdGeometryInfo,
  NavigationEvent
} from '../../domain/ad-container.domain';
import { isInternalSdk } from '../../js/environment-utils';
import { hashString } from '../../js/utils';
import { DebugLog } from '../../js/debug-log';
import { ConfigStore } from '../../js/stores/config.store';

export class MraidAdapter implements IAdContainerPort {
  msAdsOptions: Object;
  configStore: ConfigStore;

  stateListeners: Array<StateListener> = [];
  viewabilityListeners: Array<ViewabilityListener> = [];
  exposureListeners: Array<ExposureListener> = [];
  onErrorListeners: Array<ErrorListener> = [];
  sizeChangeListeners: Array<SizeChangeListener> = [];
  navigationListeners: Array<NavigationEventListener> = [];

  constructor(msAdsOptions: Object, configStore: ConfigStore) {
    this.msAdsOptions = msAdsOptions;
    this.configStore = configStore;

    this.listenStateChange();
    this.listenViewabilityChange();
    this.listenExposureChange();
    this.listenOnError();
    this.listenSizeChange();
    // ogyOnNavigation is Ogury-specific; only register when running on Ogury SDK to avoid MRAID addEventListener errors on external SDKs
    if (isInternalSdk()) {
      this.listenNavigationEvent();
    }

    this._setOrientationPolicy();
  }

  // Safely execute a MRAID command with parameters
  execMraidCmd(cmd: string, ...params: Array<any>): any {
    DebugLog.log(`MraidAdapter execMraidCmd called ${cmd}`, { params });

    if (!this.hasCmd(cmd)) {
      DebugLog.log(
        `MraidAdapter execMraidCmd failed - command '${cmd}' not available`
      );
      return;
    }

    try {
      const result = (window.mraid as any)[cmd](...params);
      DebugLog.log(`MraidAdapter execMraidCmd succeeded ${cmd}`, { result });
      return result;
    } catch (error) {
      console.warn(`MRAID command '${cmd}' failed:`, error);
      return undefined;
    }
  }

  hasCmd(cmd: string): boolean {
    if (!window.mraid || typeof (window.mraid as any)[cmd] !== 'function') {
      return false;
    }
    return true;
  }

  getVersion(): number {
    const version = this.execMraidCmd('getVersion');
    return version ? parseFloat(version) : 0;
  }

  hasBeenPolyfilled(): boolean {
    return !!(window.mraid && window.mraid.__ogyPolyfill);
  }

  getState(): AdContainerState {
    return this.execMraidCmd('getState') || 'loading';
  }

  isViewable(): boolean {
    if (this.getState() === 'loading') return false;
    const isViewable = this.execMraidCmd('isViewable');
    return isViewable === true || isViewable === 'true';
  }

  getAdGeometryInfo(): AdGeometryInfo {
    const defaultGeometry: MRAIDPosition = {
      width: 0,
      height: 0,
      x: 0,
      y: 0
    };

    const getGeometry = (method: string) => {
      if (this.getState() === 'loading') return defaultGeometry;
      return this.execMraidCmd(method) || defaultGeometry;
    };

    return {
      viewport: getGeometry('getScreenSize'),
      currentAdPosition: getGeometry('getCurrentPosition'),
      initialAdPosition: getGeometry('getDefaultPosition')
    };
  }

  onStateChange(listener: StateListener) {
    this.stateListeners.push(listener);
  }

  onViewabilityChange(listener: ViewabilityListener) {
    this.viewabilityListeners.push(listener);
  }

  onExposureChange(listener: ExposureListener) {
    this.exposureListeners.push(listener);
  }

  onError(listener: ErrorListener): void {
    this.onErrorListeners.push(listener);
  }

  onSizeChange(listener: SizeChangeListener): void {
    this.sizeChangeListeners.push(listener);
  }

  onNavigationEvent(listener: NavigationEventListener): void {
    this.navigationListeners.push(listener);
  }

  openAd(url: string): void {
    this.execMraidCmd('open', url);
  }

  listenStateChange(): void {
    DebugLog.log('MraidAdapter listenStateChange called');
    this.addEventListener('stateChange', (newState: string) => {
      DebugLog.log('MraidAdapter stateChange event received', { newState });
      this.stateListeners.forEach(stateListener =>
        stateListener(newState as AdContainerState)
      );
      DebugLog.log('MraidAdapter stateChange listeners notified', {
        listenersCount: this.stateListeners.length
      });
    });
  }

  listenViewabilityChange(): void {
    const listener = (isViewable: boolean) => {
      if (!isViewable) isViewable = this.isViewable();

      this.viewabilityListeners.forEach(viewabilityListener =>
        viewabilityListener(isViewable)
      );
    };

    // @TODO: check if it still needed for AppLovin
    // Override toString method to fix Mopub addEventListerner implementation
    // Mopub use String(listener) to compare if the listener function exists or not
    // We hash the callback function to ensure that this mecanism is still working but
    // we only consider that the callback function must be different
    const listernerString = listener.toString();
    listener.toString = (): string => `fct${hashString(listernerString)}`;

    this.addEventListener('viewableChange', listener);
  }

  listenExposureChange(): void {
    if (this.getVersion() < 3) return;

    this.addEventListener(
      'exposureChange',
      (
        exposedPercentage: number,
        visibleRectangle?: MRAIDPosition,
        occlusionRectangles?: MRAIDPosition[]
      ) =>
        this.exposureListeners.forEach(exposureListener =>
          exposureListener({
            percentageInView: this.convertToNumber(exposedPercentage),
            visibleRectangle,
            occlusionRectangles
          })
        )
    );
  }

  listenSizeChange(): void {
    this.addEventListener('sizeChange', (width: number, height: number) => {
      this.sizeChangeListeners.forEach(sizeChangeListener =>
        sizeChangeListener({ width, height })
      );
    });
  }

  listenNavigationEvent(): void {
    this.addEventListener('ogyOnNavigation', (event: NavigationEvent) => {
      this.navigationListeners.forEach(navigationListener =>
        navigationListener(event)
      );
    });
  }

  convertToNumber(value: string | number): number {
    const num = typeof value === 'number' ? value : parseInt(value, 10);
    return isNaN(num) ? 0 : num;
  }

  listenOnError(): void {
    this.addEventListener('error', (message: string, action: string) => {
      this.onErrorListeners.forEach(onErrorListener =>
        onErrorListener({
          message,
          action
        })
      );
    });
  }

  disableContainerCloseButton(disable: boolean): void {
    this.executeWhenReady(() => {
      this.execMraidCmd('useCustomClose', disable);
    });
  }

  // Ensure that the callback is executed when MRAID is ready
  executeWhenReady(cb: Function): void {
    if (this.getState() === 'loading') {
      this.addEventListener('ready', cb);
    } else {
      cb();
    }
  }

  _setOrientationPolicy() {
    this.executeWhenReady(() => {
      const { orientation } = this.configStore.config;
      if (orientation && orientation !== 'auto') {
        this.execMraidCmd('setOrientationProperties', {
          allowOrientationChange: false,
          forceOrientation: orientation
        });
      }
    });
  }

  close(param: any) {
    if (typeof param === 'undefined') {
      this.execMraidCmd('close');
    } else {
      this.execMraidCmd('close', param);
    }
  }

  expand(url?: string) {
    this.execMraidCmd('expand', url);
  }

  unload() {
    this.execMraidCmd('unload');
  }

  getMaxSize(): MRAIDSize {
    const defaultSize = { width: 0, height: 0 };
    if (this.getState() === 'loading') {
      return defaultSize;
    }
    return this.execMraidCmd('getMaxSize') || defaultSize;
  }

  getScreenSize() {
    const defaultSize = { width: 0, height: 0 };
    if (this.getState() === 'loading') {
      return defaultSize;
    }
    return this.execMraidCmd('getScreenSize') || defaultSize;
  }

  getCurrentPosition(): MRAIDPosition {
    const defaultPosition = { x: 0, y: 0, width: 0, height: 0 };
    if (this.getState() === 'loading') {
      return defaultPosition;
    }
    return this.execMraidCmd('getCurrentPosition') || defaultPosition;
  }

  getDefaultPosition(): MRAIDPosition {
    const defaultPosition = { x: 0, y: 0, width: 0, height: 0 };
    if (this.getState() === 'loading') {
      return defaultPosition;
    }
    return this.execMraidCmd('getDefaultPosition') || defaultPosition;
  }

  getPlacementType() {
    if (this.getState() === 'loading') {
      return 'inline';
    }
    return this.execMraidCmd('getPlacementType') || 'inline';
  }

  // ============ Event management ============

  addEventListener(event: string, callback: Function) {
    this.execMraidCmd('addEventListener', event, callback);
  }

  removeEventListener(event: string, callback: Function) {
    this.execMraidCmd('removeEventListener', event, callback);
  }

  // ============ Ogury specific methods ============

  ogyCreateWebView(
    options: WebViewProperties,
    callback: (err?: Error, res?: { webviewId: string }) => void
  ) {
    this.execMraidCmd('ogyCreateWebView', options, callback);
  }

  ogyCloseWebView(
    webViewId: string,
    callback?: (err?: Error, res?: Object) => void
  ) {
    this.execMraidCmd('ogyCloseWebView', webViewId, callback);
  }

  ogyCreateShortcut(
    options: Object,
    callback: (err?: Error, res?: Object) => void
  ) {
    this.execMraidCmd('ogyCreateShortcut', options, callback);
  }

  ogyResolveIntent(intentUri: string, callback: OgyResolveIntentCallback) {
    this.execMraidCmd('ogyResolveIntent', intentUri, callback);
  }

  ogyStartIntent(
    intentUri: string,
    callback: (err?: Error, res?: Object) => void
  ) {
    this.execMraidCmd('ogyStartIntent', intentUri, callback);
  }

  setOrientationProperties(properties: Object) {
    this.execMraidCmd('setOrientationProperties', properties);
  }

  ogyOnAdEvent(event: string) {
    this.execMraidCmd('ogyOnAdEvent', event);
  }

  ogyForceClose() {
    this.execMraidCmd('ogyForceClose');
  }

  ogyVideoAutoplayError() {
    this.execMraidCmd('ogyVideoAutoplayError');
  }

  ogyOnAdImpression() {
    this.execMraidCmd('ogyOnAdImpression');
  }

  ogyOnAdLoaded() {
    this.executeWhenReady(() => this.execMraidCmd('ogyOnAdLoaded'));
  }

  ogyOnAdClicked() {
    this.execMraidCmd('ogyOnAdClicked');
  }

  ogyOpenStoreKit() {
    this.execMraidCmd('ogyOpenStoreKit');
  }

  ogyOpenSKOverlay() {
    this.execMraidCmd('ogyOpenSKOverlay');
  }

  ogyUpdateWebView(
    webViewId: string,
    options: WebViewUpdateProperties,
    callback?: (err?: Error, res?: Object) => void
  ) {
    this.execMraidCmd('ogyUpdateWebView', webViewId, options, callback);
  }

  ogyNavigateBack(webViewId: string) {
    this.execMraidCmd('ogyNavigateBack', webViewId);
  }

  ogyNavigateForward(webViewId: string) {
    this.execMraidCmd('ogyNavigateForward', webViewId);
  }
}
